import stordLogo from "/img/integration/stord.png"
import { ConnectLogo } from "../connect-logo"
import { Form } from "@/components/form/Form.tsx"
import { Input, PasswordInput } from "@/components/form/Input.tsx"
import { Button } from "@/components/ui/button.tsx"
import { useState } from "preact/compat"
import { useLocation } from "wouter-preact"
import { use_connect_redirect_to } from "../use_connect_redirect_to"
import { CenterColumnContainer } from "@/components/LoginScreen.tsx"
import { getStordConfig, updateStordConfig } from "@/api/organization"
import { useAsyncEffect } from "@/utils"
import { Alert } from "@/components/type"

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function ConnectStordPage(props: any = {}) {
  const redirectTo = use_connect_redirect_to(props.redirectTo)
  const [, setLocation] = useLocation()
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [formErrors, setFormErrors] = useState<Alert[] | null>(null)
  const [hasSavedApiKey, setHasSavedApiKey] = useState(false)
  const [apiKey, setApiKey] = useState("")
  const [stordOrganizationId, setStordOrganizationId] = useState("")
  const [stordNetworkId, setStordNetworkId] = useState("")

  useAsyncEffect(async () => {
    const res = await getStordConfig()
    if (!res.ok) {
      setLoadError("Could not load Stord settings")
      return
    }
    setHasSavedApiKey(res.value.data.has_api_key)
    setStordOrganizationId(res.value.data.stord_organization_id ?? "")
    setStordNetworkId(res.value.data.stord_network_id ?? "")
  }, [])

  async function onSubmit(e: Event) {
    e.preventDefault()
    setLoading(true)
    setFormErrors(null)
    setLoadError(null)

    const trimmedOrgId = stordOrganizationId.trim()
    const trimmedNetworkId = stordNetworkId.trim()
    const trimmedApiKey = apiKey.trim()

    if (!trimmedOrgId || !trimmedNetworkId) {
      setFormErrors([{ message: "Stord organization ID and network ID are required.", type: "danger" }])
      setLoading(false)
      return
    }

    if (!hasSavedApiKey && !trimmedApiKey) {
      setFormErrors([{ message: "API key is required.", type: "danger" }])
      setLoading(false)
      return
    }

    const res = await updateStordConfig({
      api_key: trimmedApiKey || undefined,
      stord_organization_id: trimmedOrgId,
      stord_network_id: trimmedNetworkId,
    })

    setLoading(false)
    if (!res.ok) {
      setFormErrors([{ message: "Could not save Stord settings. Please try again.", type: "danger" }])
      return
    }
    setLocation(redirectTo)
  }

  return (
    <CenterColumnContainer>
      <Form class="space-y-6" onSubmit={onSubmit} alerts={formErrors}>
        <ConnectLogo src={stordLogo} alt="Stord" />
        <div class="space-y-3 text-gray-500 text-sm">
          <p>
            Connect Stord to request bills of lading by purchase order from deduction detail pages.
          </p>
          <p>
            You'll need a <strong class="text-gray-700">Stord API key</strong> (Bearer token — no separate secret),
            plus your <strong class="text-gray-700">Stord organization ID</strong> and{" "}
            <strong class="text-gray-700">network ID</strong>. Access is typically obtained by reaching out to someone
            on the Stord team.
          </p>
          <p>
            Questions? Email{" "}
            <a href="mailto:joey@marginwiz.com" class="text-gray-900 underline underline-offset-2">
              joey@marginwiz.com
            </a>{" "}
            or use the chat in the bottom-right corner.
          </p>
        </div>

        {loadError ? <p class="text-sm text-red-600">{loadError}</p> : null}

        <PasswordInput
          label="API key"
          name="stord_api_key"
          required={!hasSavedApiKey}
          value={apiKey}
          onChange={e => setApiKey(e.currentTarget.value)}
          noAutofill
        />
        {hasSavedApiKey && !apiKey ? (
          <p class="text-xs text-gray-400 -mt-4">API key saved — enter a new value to replace it.</p>
        ) : null}

        <Input
          label="Stord organization ID"
          type="text"
          name="stord_organization_id"
          required={true}
          value={stordOrganizationId}
          onChange={e => setStordOrganizationId(e.currentTarget.value)}
          noAutofill
        />
        <Input
          label="Stord network ID"
          type="text"
          name="stord_network_id"
          required={true}
          value={stordNetworkId}
          onChange={e => setStordNetworkId(e.currentTarget.value)}
          noAutofill
        />

        <div class="flex justify-end">
          <Button
            type="submit"
            class="bg-gray-900 text-white rounded-lg hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:ring-offset-2 disabled:bg-gray-300 disabled:cursor-not-allowed"
            disabled={loading || !!loadError}>
            {loading ? "Saving..." : "Save"}
          </Button>
        </div>
      </Form>
    </CenterColumnContainer>
  )
}
